import { useCallback, useEffect } from 'react'
import { useAuthStore } from '@/stores/authStore'

/**
 * Authentication hook.
 *
 * Provides access to auth state and methods.
 * Handles redirects for unauthenticated users.
 */
export function useAuth() {
  const {
    token,
    username,
    isAuthenticated,
    branch,
    initialize,
    getToken,
    getUsername,
    setToken,
    setUsername,
    setBranch,
    logout: storeLogout,
  } = useAuthStore()

  // Re-initialize on mount to pick up any changes from v1
  useEffect(() => {
    initialize()
  }, [initialize])

  // Logout and redirect to v1 login
  const logout = useCallback(() => {
    storeLogout()
    // Redirect to v1 logout page
    window.location.href = '/logout'
  }, [storeLogout])

  // Redirect to v1 login if not authenticated
  const redirectToLogin = useCallback(() => {
    // Save current path for return after login
    sessionStorage.setItem('destination', window.location.pathname)
    window.location.href = '/'
  }, [])

  return {
    token,
    username,
    isAuthenticated,
    branch,
    getToken,
    getUsername,
    setToken,
    setUsername,
    setBranch,
    logout,
    redirectToLogin,
  }
}

/**
 * Hook that requires authentication.
 *
 * Use this in protected components to ensure the user is logged in.
 * Redirects to login if not authenticated.
 */
export function useRequireAuth() {
  const auth = useAuth()

  useEffect(() => {
    if (!auth.isAuthenticated) {
      auth.redirectToLogin()
    }
  }, [auth])

  return auth
}

/**
 * Hook to get the current user's profile URL.
 */
export function useUserProfileUrl() {
  const { username } = useAuth()
  return username ? `/user/${username}` : null
}

/**
 * Hook to get the v1 base URL for the current branch.
 */
export function useV1BaseUrl() {
  const { branch } = useAuth()
  return `https://${branch}.alpha-trader.com`
}
